/**
 * Note quads in Phira's draw order (W1-06; R§4.8, R§10, S§3.5, A§7.2).
 *
 * Phira buffers note quads in a map keyed by (kind order, GL texture) and flushes it once after all lines:
 * Hold 0 < Drag 1 < Tap 2 < Flick 3, and inside a kind by texture creation order (click, hold, flick, drag, the
 * MH set, then the hold-repeat bodies, = NOTE_TEX_SLOTS order). Inside one bucket quads keep their push order:
 * lines in (zOrder, export index) order, and inside a line the walk order of culling.ts. Later quads are on top.
 * Vertex colours are bytes, clamped explicitly (a Uint8Array would wrap modulo 256).
 * In judged play the Bad-note ghosts (badNotes.ts) follow as trailing buckets with kind order BAD_GHOST_KIND_ORDER.
 */

import type { EngineCompileContext, FrameDrawListBuilder, FrameOptions, LineStateRt, NoteQuadBatch } from '../types';
import { NOTE_KIND_ORDER } from '../types';
import { BAD_GHOST_KIND_ORDER, pushBadGhosts, type BadGhostSink } from './badNotes';
import { cullLimits, walkLineNotes, type CullLimits, type NoteWalkVisitor } from './culling';
import {
  frameEnv,
  makeLineEnv,
  NoteEval,
  setLineCullFlags,
  setLineEnv,
  SLOT_CLICK,
  SLOT_DRAG,
  SLOT_FLICK,
  type FrameEnv,
  type LineEnv,
} from './env';
import { refreshMultiFlags } from './multiHint';
import { evalNote, surelyOffscreen } from './noteGeom';
import type { LineNotesRt } from './runtime';
import { cacheOf, syncAll, syncedRt, type NotesGlobal } from './store';
import { dimMultiplier, isGone, lineNotesHiddenReason } from './visibility';

export { dimMultiplier };

/** Kind order of every texture slot (NOTE_TEX_SLOTS index -> NOTE_KIND_ORDER value). */
export const SLOT_KIND_ORDER: readonly number[] = [
  NOTE_KIND_ORDER.tap,
  NOTE_KIND_ORDER.hold,
  NOTE_KIND_ORDER.flick,
  NOTE_KIND_ORDER.drag,
  NOTE_KIND_ORDER.tap,
  NOTE_KIND_ORDER.hold,
  NOTE_KIND_ORDER.flick,
  NOTE_KIND_ORDER.drag,
  NOTE_KIND_ORDER.hold,
  NOTE_KIND_ORDER.hold,
];

/** Slots in bucket draw order: (kind order asc, slot rank asc). */
export const BUCKET_SLOT_ORDER: readonly number[] = SLOT_KIND_ORDER.map((_, s) => s).sort(
  (a, b) => (SLOT_KIND_ORDER[a] ?? 0) - (SLOT_KIND_ORDER[b] ?? 0) || a - b,
);

/** Slots of the Bad-note ghost buckets in draw order (normal textures of the non-hold kinds, slot rank asc). */
export const GHOST_SLOT_ORDER: readonly number[] = [SLOT_CLICK, SLOT_FLICK, SLOT_DRAG];

/** A fresh, empty batch with room for `capacity` quads. */
export function createNoteQuadBatch(capacity = 256): NoteQuadBatch {
  const cap = Math.max(1, capacity);
  return {
    count: 0,
    pos: new Float32Array(cap * 8),
    uv: new Float32Array(cap * 4),
    color: new Uint8Array(cap * 4),
    slot: new Uint8Array(cap),
    note: new Int32Array(cap),
    buckets: [],
  };
}

/** Growable per-slot quad buffer. */
class SlotBuffer {
  count = 0;
  pos = new Float32Array(64 * 8);
  uv = new Float32Array(64 * 4);
  color = new Uint8Array(64 * 4);
  note = new Int32Array(64);

  reserve(n: number): void {
    if (n <= this.note.length) return;
    const cap = Math.max(n, this.note.length * 2);
    const pos = new Float32Array(cap * 8);
    pos.set(this.pos.subarray(0, this.count * 8));
    const uv = new Float32Array(cap * 4);
    uv.set(this.uv.subarray(0, this.count * 4));
    const color = new Uint8Array(cap * 4);
    color.set(this.color.subarray(0, this.count * 4));
    const note = new Int32Array(cap);
    note.set(this.note.subarray(0, this.count));
    this.pos = pos;
    this.uv = uv;
    this.color = color;
    this.note = note;
  }
}

function pushInto(b: SlotBuffer, ev: NoteEval, q: number, note: number): void {
  b.reserve(b.count + 1);
  const k = b.count;
  for (let j = 0; j < 8; j++) b.pos[k * 8 + j] = ev.pos[q * 8 + j] ?? 0;
  for (let j = 0; j < 4; j++) b.uv[k * 4 + j] = ev.uv[q * 4 + j] ?? 0;
  b.color[k * 4] = ev.r;
  b.color[k * 4 + 1] = ev.g;
  b.color[k * 4 + 2] = ev.b;
  b.color[k * 4 + 3] = ev.alphaByte;
  b.note[k] = note;
  b.count = k + 1;
}

/**
 * Collects quads per texture slot and concatenates them into a NoteQuadBatch in bucket order: the note buckets,
 * then the Bad-note ghost buckets of judged play.
 */
export class QuadSink implements BadGhostSink {
  private readonly buffers: SlotBuffer[] = [];
  /** Bad-note ghosts per slot (only the normal non-hold slots are used). */
  private readonly ghostBuffers: SlotBuffer[] = [];

  constructor() {
    for (let s = 0; s < SLOT_KIND_ORDER.length; s++) this.buffers.push(new SlotBuffer());
    for (let s = 0; s < SLOT_KIND_ORDER.length; s++) this.ghostBuffers.push(new SlotBuffer());
  }

  reset(): void {
    for (const b of this.buffers) b.count = 0;
    for (const b of this.ghostBuffers) b.count = 0;
  }

  /** Pushes quad `q` of `ev` (corners, UV and slot from the evaluation, colour bytes from it). */
  pushFrom(ev: NoteEval, q: number, note: number): void {
    pushInto(this.buffers[ev.slot[q] ?? 0] as SlotBuffer, ev, q, note);
  }

  /** Pushes a Bad-note ghost quad (note id -1: not a hit target). */
  pushGhost(ev: NoteEval, q: number): void {
    pushInto(this.ghostBuffers[ev.slot[q] ?? 0] as SlotBuffer, ev, q, -1);
  }

  /** Total quads collected. */
  get total(): number {
    let n = 0;
    for (const b of this.buffers) n += b.count;
    for (const b of this.ghostBuffers) n += b.count;
    return n;
  }

  /** Writes the collected quads into `out.notes` (replacing its content; grows the batch when needed). */
  flush(out: FrameDrawListBuilder): void {
    const total = this.total;
    let batch = out.notes;
    if (batch.note.length < total) {
      batch = createNoteQuadBatch(Math.max(total, batch.note.length * 2));
      out.notes = batch;
    }
    batch.buckets.length = 0;
    let at = 0;
    const append = (b: SlotBuffer, slot: number, kindOrder: number): void => {
      if (b.count === 0) return;
      batch.pos.set(b.pos.subarray(0, b.count * 8), at * 8);
      batch.uv.set(b.uv.subarray(0, b.count * 4), at * 4);
      batch.color.set(b.color.subarray(0, b.count * 4), at * 4);
      batch.note.set(b.note.subarray(0, b.count), at);
      batch.slot.fill(slot, at, at + b.count);
      batch.buckets.push({ kindOrder, slot, start: at, count: b.count });
      at += b.count;
    };
    for (const slot of BUCKET_SLOT_ORDER) append(this.buffers[slot] as SlotBuffer, slot, SLOT_KIND_ORDER[slot] ?? 0);
    for (const slot of GHOST_SLOT_ORDER) append(this.ghostBuffers[slot] as SlotBuffer, slot, BAD_GHOST_KIND_ORDER);
    batch.count = at;
  }
}

/**
 * Line states in Phira's line draw order (zOrder, export index); attachUI lines are kept (callers skip them).
 * The permutation is cached and re-sorted only when a zOrder or the state order changed.
 */
export function linesInDrawOrder(ctx: EngineCompileContext, g: NotesGlobal, states: readonly LineStateRt[]): number[] {
  const cache = cacheOf(g, 'drawOrder', () => ({
    z: new Float64Array(0),
    idx: new Int32Array(0),
    order: [] as number[],
  }));
  const n = states.length;
  let same = cache.z.length === n;
  const z = same ? cache.z : new Float64Array(n);
  const idx = same ? cache.idx : new Int32Array(n);
  for (let k = 0; k < n; k++) {
    const s = states[k] as LineStateRt;
    const zo = syncedRt(ctx, s.line).ir.zOrder;
    if (z[k] !== zo || idx[k] !== s.index) same = false;
    z[k] = zo;
    idx[k] = s.index;
  }
  if (!same || cache.order.length !== n) {
    const order: number[] = [];
    for (let k = 0; k < n; k++) order.push(k);
    order.sort((a, b) => (z[a] ?? 0) - (z[b] ?? 0) || (idx[a] ?? 0) - (idx[b] ?? 0));
    cache.z = z;
    cache.idx = idx;
    cache.order = order;
  }
  return cache.order;
}

/** Visitor of the quad pass (one instance per engine; fields are set per frame / per line). */
export class QuadPass implements NoteWalkVisitor {
  /** `visit` returns at once for gone notes (the walk may skip a leading run of them). */
  readonly ignoresGone = true;
  ctx!: EngineCompileContext;
  fe!: FrameEnv;
  readonly le: LineEnv = makeLineEnv();
  readonly sink = new QuadSink();
  readonly ev = new NoteEval();
  readonly limits: CullLimits = { above: 0, below: 0 };

  visit(rt: LineNotesRt, i: number): void {
    if (isGone(rt, i, this.fe.t, this.fe.judge)) return;
    // Holds and fakes are visited however far away they are: skip the ones that surely draw nothing.
    if (rt.plain[i] !== 1 && surelyOffscreen(this.fe, this.le, rt, i)) return;
    this.pushEvaluated(rt, i, evalNote(this.ctx, this.fe, this.le, i, false, this.ev));
  }

  /** Pushes the quads Phira draws for note i, evaluated into `ev` (nothing when covered). */
  pushEvaluated(rt: LineNotesRt, i: number, ev: NoteEval): void {
    if (ev.covered) return;
    const id = rt.id[i] ?? -1;
    for (let q = 0; q < ev.quads; q++) if (ev.offscreen[q] === 0) this.sink.pushFrom(ev, q, id);
  }

  /** Sets the line of the walk that follows (line env, early-cull flags, aggressive culling limits). */
  startLine(rt: LineNotesRt, state: LineStateRt, filters: FrameOptions['filters']): void {
    setLineEnv(this.le, rt, state.m, state.height, state.inclineSin, dimMultiplier(filters, state.line));
    setLineCullFlags(this.le, this.ctx);
    cullLimits(state.m, this.fe.ar, this.limits);
  }
}

/** The engine's quad pass, reset for a frame at t. */
export function quadPassFor(
  ctx: EngineCompileContext,
  g: NotesGlobal,
  t: number,
  opts: FrameOptions,
  out: FrameDrawListBuilder,
): QuadPass {
  const pass = cacheOf(g, 'quadPass', () => new QuadPass());
  pass.ctx = ctx;
  pass.fe = frameEnv(t, out.viewport.ar, opts.resAlpha, opts.player, opts.skin, opts.judge, ctx.pkg);
  pass.sink.reset();
  return pass;
}

/** Appends the judged-play Bad ghosts and writes the batch into `out.notes`. */
export function finishQuadPass(
  ctx: EngineCompileContext,
  pass: QuadPass,
  t: number,
  opts: FrameOptions,
  out: FrameDrawListBuilder,
): void {
  pushBadGhosts(ctx, t, opts, pass.fe.ar, pass.sink);
  pass.sink.flush(out);
}

/** Fills `out.notes` with every note quad Phira draws at t. */
export function pushNoteQuads(
  ctx: EngineCompileContext,
  states: readonly LineStateRt[],
  t: number,
  opts: FrameOptions,
  out: FrameDrawListBuilder,
): void {
  const g = syncAll(ctx);
  const pass = quadPassFor(ctx, g, t, opts, out);
  const filters = opts.filters;
  for (const k of linesInDrawOrder(ctx, g, states)) {
    const state = states[k] as LineStateRt;
    if (lineNotesHiddenReason(state, filters) !== null) continue;
    const rt = syncedRt(ctx, state.line);
    if (rt.n === 0) continue;
    refreshMultiFlags(rt, g.mh);
    pass.startLine(rt, state, filters);
    walkLineNotes(rt, state.height, pass.limits, opts.player.aggressiveCull, t, opts.judge, pass);
  }
  finishQuadPass(ctx, pass, t, opts, out);
}
